import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "setsField", "repsField", "weightField", "weightHeader", "previousHeader"]
  static values = { sets: Number, reps: Number, weight: Number, bodyweight: Boolean, exerciseId: Number }

  connect() {
    this.previousSets = []
    this.renderInitialRows()
    this.syncFields()
    this.toggleColumns()

    // On an existing plan the exercise never "changes", so the history has to
    // be fetched here too — otherwise the weight placeholder falls back to 0
    // even when previous sessions were logged.
    if (this.exerciseIdValue) {
      this.fetchPreviousSets(this.exerciseIdValue).then(() => this.rerenderRows())
    }
  }

  async handleEquipmentChanged(event) {
    const equipment = event.detail.equipment
    const exerciseId = event.detail.exerciseId
    this.bodyweightValue = equipment === "body_weight"
    this.toggleColumns()

    if (exerciseId) {
      await this.fetchPreviousSets(exerciseId)
    }

    this.rerenderRows()
  }

  async fetchPreviousSets(exerciseId) {
    try {
      const response = await fetch(`/athlete/exercises/${exerciseId}/last_sets`, {
        headers: { "Accept": "application/json" }
      })
      this.previousSets = await response.json()
    } catch {
      this.previousSets = []
    }
  }

  toggleColumns() {
    if (this.hasWeightHeaderTarget) {
      this.weightHeaderTarget.classList.toggle("hidden", this.bodyweightValue)
    }
    if (this.hasPreviousHeaderTarget) {
      this.previousHeaderTarget.classList.toggle("hidden", !this.bodyweightValue)
    }
  }

  rerenderRows() {
    const rows = this.containerTarget.querySelectorAll("[data-set-row]")
    const data = []
    rows.forEach((row) => {
      const weight = row.querySelector("[data-set-weight]")?.value || ""
      const reps = row.querySelector("[data-set-reps]")?.value || ""
      data.push({ weight, reps })
    })
    this.containerTarget.innerHTML = ""
    data.forEach((d, i) => this.insertRow(i + 1, d.weight, d.reps))
  }

  renderInitialRows() {
    const count = this.setsValue || 1
    for (let i = 0; i < count; i++) {
      this.insertRow(i + 1, this.weightValue || "", this.repsValue || "")
    }
  }

  addSet(event) {
    event.preventDefault()
    const currentCount = this.containerTarget.querySelectorAll("[data-set-row]").length
    this.insertRow(currentCount + 1, "", "")
    this.syncFields()
  }

  removeSet(event) {
    event.preventDefault()
    const rows = this.containerTarget.querySelectorAll("[data-set-row]")
    if (rows.length <= 1) return

    const row = event.target.closest("[data-set-row]")
    if (row) {
      row.remove()
      this.renumber()
      this.syncFields()
    }
  }

  syncFields() {
    const rows = this.containerTarget.querySelectorAll("[data-set-row]")
    const count = rows.length

    this.setsFieldTarget.value = count

    let lastReps = ""
    let lastWeight = ""

    rows.forEach((row) => {
      const repsInput = row.querySelector("[data-set-reps]")
      const weightInput = row.querySelector("[data-set-weight]")
      if (repsInput && repsInput.value) lastReps = repsInput.value
      if (weightInput && weightInput.value) lastWeight = weightInput.value
    })

    if (lastReps) this.repsFieldTarget.value = lastReps
    if (lastWeight) this.weightFieldTarget.value = lastWeight

    this.clearErrorsIfValid()
  }

  clearErrorsIfValid() {
    const card = this.element
    if (!card.hasAttribute("data-has-errors")) return

    const exerciseId = card.querySelector("input[name*='[exercise_id]']")
    const hasExercise = exerciseId && exerciseId.value
    const hasReps = this.repsFieldTarget.value && parseInt(this.repsFieldTarget.value) > 0
    const hasSets = this.setsFieldTarget.value && parseInt(this.setsFieldTarget.value) > 0

    if (hasExercise && hasReps && hasSets) {
      card.removeAttribute("data-has-errors")
      card.classList.remove("bg-red-light", "border-red/40")
      card.classList.add("bg-gray-50", "border-gray-border")

      const errorBlock = card.querySelector("[data-error-block]")
      if (errorBlock) errorBlock.remove()

      this.clearBannerIfNoErrors()
    }
  }

  clearBannerIfNoErrors() {
    if (document.querySelector("[data-has-errors]")) return

    const banner = document.querySelector("[data-form-error-banner]")
    if (banner) banner.remove()
  }

  autofill(event) {
    const changedInput = event.target
    const value = changedInput.value
    if (!value) return

    const fieldType = changedInput.hasAttribute("data-set-weight") ? "weight" : "reps"
    const selector = fieldType === "weight" ? "[data-set-weight]" : "[data-set-reps]"

    const rows = this.containerTarget.querySelectorAll("[data-set-row]")
    rows.forEach((row) => {
      const input = row.querySelector(selector)
      if (input && !input.value) {
        input.value = value
      }
    })

    this.syncFields()
  }

// Weight of the last set that carried one in the previous session.
  lastLoggedWeight() {
    return [...this.previousSets].reverse().find((s) => s?.weight)?.weight
  }

  insertRow(number, weight, reps) {
    const row = document.createElement("div")
    row.setAttribute("data-set-row", "")
    row.className = "flex items-center gap-3"

    const prev = this.previousSets[number - 1]

    let previousCol = ""
    if (this.bodyweightValue) {
      const prevReps = prev?.reps
      previousCol = `
        <div class="w-14 flex-shrink-0 text-center">
          <span class="text-[11px] text-navy-light">${prevReps ? prevReps + " reps" : "—"}</span>
        </div>`
    }

    // Placeholder shows what was actually lifted last time: this set's own
    // weight when that set existed, otherwise the last logged one.
    const previousWeight = prev?.weight ?? this.lastLoggedWeight()

    const weightInput = this.bodyweightValue ? "" : `
      <div class="flex-1">
        <input type="number"
               data-set-weight
               value="${weight}"
               placeholder="${previousWeight ?? 0}"
               min="0"
               step="0.5"
               inputmode="decimal"
               data-action="change->exercise-sets#syncFields change->exercise-sets#autofill"
               class="w-full px-3 py-2 rounded-md border border-gray-border bg-white text-sm input-no-zoom text-navy focus:border-brand focus:ring-1 focus:ring-brand outline-none transition-colors placeholder:text-gray-muted text-center">
      </div>`

    row.innerHTML = `
      <span class="w-8 text-center text-xs font-medium text-navy-light" data-set-number>${number}</span>
      ${previousCol}
      ${weightInput}
      <div class="flex-1">
        <input type="number"
               data-set-reps
               value="${reps}"
               placeholder="0"
               min="1"
               step="1"
               inputmode="numeric"
               data-action="change->exercise-sets#syncFields change->exercise-sets#autofill"
               class="w-full px-3 py-2 rounded-md border border-gray-border bg-white text-sm input-no-zoom text-navy focus:border-brand focus:ring-1 focus:ring-brand outline-none transition-colors placeholder:text-gray-muted text-center">
      </div>
      <button type="button"
              data-action="click->exercise-sets#removeSet"
              class="p-1 text-navy-light hover:text-red transition-colors cursor-pointer"
              title="Remover série">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
    `

    this.containerTarget.appendChild(row)
  }

  renumber() {
    const rows = this.containerTarget.querySelectorAll("[data-set-row]")
    rows.forEach((row, index) => {
      const numberEl = row.querySelector("[data-set-number]")
      if (numberEl) numberEl.textContent = index + 1
    })
  }
}